import { Button, Text, VStack } from '@chakra-ui/react';
import { useUpdateProcedureMutation } from '@services/data/procedure.api';
import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProcedureStep, ProcedureStepStateEnum } from '@/types/config/procedure.types';

import { useProcedureTabContext } from '../ProcedureTab';
import ProcedureInstructions from './ProcedureInstructions';
import StepActionButtons from './StepActionButtons';

type Props = {
  step: ProcedureStep;
  procedureId: string;
};

function ConfirmationStep({ step, procedureId }: Readonly<Props>) {
  const [state, setState] = useState<ProcedureStepStateEnum.CONFIRMED | ProcedureStepStateEnum.DENIED | null>(null);
  const [updateProcedure] = useUpdateProcedureMutation();
  const { canManage, notificationId } = useProcedureTabContext();
  if (!canManage) {
    return <ProcedureInstructions instructions={step.instructions} />;
  }

  return (
    <>
      <VStack
        paddingY={5}
        paddingX={6}
        gap={2}
        backgroundColor="neutral.700"
        width="100%"
        flex={step.instructions.length > 0 ? 0 : 1}
      >
        <Button
          variant="toggle"
          size="toggleM"
          data-active={state === ProcedureStepStateEnum.CONFIRMED}
          cursor={state === ProcedureStepStateEnum.CONFIRMED ? 'default' : 'pointer'}
          onClick={() => setState(ProcedureStepStateEnum.CONFIRMED)}
        >
          <Text>
            <FormattedMessage id="notification.procedure.step.type.CONFIRMATION.confirm" />
          </Text>
        </Button>
        <Button
          variant="toggle"
          size="toggleM"
          data-active={state === ProcedureStepStateEnum.DENIED}
          cursor={state === ProcedureStepStateEnum.DENIED ? 'default' : 'pointer'}
          onClick={() => setState(ProcedureStepStateEnum.DENIED)}
        >
          <Text>
            <FormattedMessage id="notification.procedure.step.type.CONFIRMATION.deny" />
          </Text>
        </Button>
      </VStack>
      <ProcedureInstructions instructions={step.instructions} />
      <StepActionButtons
        isDisabled={state === null}
        isMandatory={step.mandatory}
        procedureId={procedureId}
        stepRank={step.rank}
        onValidate={() => updateProcedure({ notificationId, procedureId, step: step.rank, state: state! }).unwrap()}
      />
    </>
  );
}

export default memo(ConfirmationStep);
